// REACT IMPORTS
import {
  useEffect,
  useRef,
  useState,
  ChangeEvent,
  KeyboardEvent,
} from "react";

//STORE IMPORT
import {
  accountDataUpdate,
  addNotification,
  contactVerification,
  updateProfilePicture,
  updateUserData,
  useUserData,
} from "store/slices/authSlice";
import UserImg from "icons/auth/user.svg";

// COMPONENTS IMPORT
import Header from "components/layout/header";

//ICONS IMPORT
import { CameraIcon } from "icons/auth/Camera";
import { MobileIcon } from "icons/auth/Mobile";
import { WorkIcon } from "icons/auth/Work";
import { OtherIcon } from "icons/auth/Other";
import { HomeIcon } from "icons/auth/Home";
import { LocationIcon } from "icons/auth/Location";
import { SmsIcon } from "icons/auth/Sms";

//HELPER FUNCTION IMPORTS
import {
  formatAddress,
  formatNumber,
  getInitialsLatters,
  getPhoneIcon,
  validateEmail,
} from "utils/HelperFunctions";
import InputField from "components/form/InputField";
import Button from "components/form/Button";
import InputSelect from "components/form/InputSelect";
import MobileInputNew from "components/form/MobileInputNew";
import {
  getCountryCallingCode,
  isValidPhoneNumber,
  parsePhoneNumber,
} from "react-phone-number-input";
import AddressForm, {
  ADDRESS_REQUIRED_FIELDS,
} from "components/userProfile/AddressForm";
import { InfoRedCircleIcon } from "icons/subscription/InfoRedCircleIcon";
import OTPVerificationModal from "components/userProfile/OTPVerificationModal";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { countryListGet } from "store/slices/paymentMethodSlice";
import { countryCodes } from "./countrycode";
import imageCompression from "browser-image-compression";
import { usePortalSettingsData } from "store/slices/dashboardSlice";
import { useParams } from "react-router-dom";
import { decode } from "utils/HelperFunctions";
import { SettingsValue } from "types/payments";
import {
  CountryCode,
  EditData,
  ErrorMessage,
  OptionItem,
  PhoneType,
  ShowOtpModal,
  ValueType,
} from "types/UserProfile";
import { ToastState } from "types/common";
import { PROFILE_URL } from "store/api/constApi";
import { RootState } from "store/store";
import MessageToast from "components/ui-component/MessageToast";
import { TestIdScope, useTestAttrs, useTid } from "utils/testid";

// Desktop reveals a field's "Edit" link on hover. It stays mounted and merely
// transparent (rather than `display: none`) so keyboard users can still tab to
// it; focus makes it visible again. Below `lg` the link is always shown.
const EDIT_LINK_IDLE_CLASS =
  "lg:opacity-0 lg:pointer-events-none lg:focus:!opacity-100 lg:focus:!pointer-events-auto";

// Single source for the "required" wording, shared by the on-blur check and the
// save guard so the two can never drift apart. Keys are the `isEdit` index.
const REQUIRED_MESSAGES: Record<number, string> = {
  1: "First Name is required",
  2: "Last Name is required",
  3: "Email is required",
  4: "Phone number is required",
};

// Shown when the server rejects a save (or the request never lands). Without
// these the failure was written to the console only, and the field closed as
// though it had saved.
const SAVE_FAILED_MESSAGES: Record<number, string> = {
  1: "Couldn't save your first name. Please try again.",
  2: "Couldn't save your last name. Please try again.",
  3: "Couldn't send the verification code. Please try again.",
  4: "Couldn't save your phone number. Please try again.",
  5: "Couldn't save your address. Please try again.",
};

const UserProfileContent = () => {
  const tid = useTid();
  const rootAttrs = useTestAttrs("page", "My profile page");
  const titleAttrs = useTestAttrs("title", "My profile");
  const contentAttrs = useTestAttrs("content", "Profile details");
  const avatarAttrs = useTestAttrs("avatar", "Profile avatar");
  const fieldsAttrs = useTestAttrs("fields", "Profile fields");
  const userData = useUserData();
  const dispatch = useAppDispatch();
  const { clusterId: cluster_id } = useParams();
  const clusterId = decode(cluster_id || "", true);
  const photoInput = useRef<HTMLInputElement | null>(null);
  const addressSectionRef = useRef<HTMLDivElement | null>(null);
  const [value, setValue] = useState<ValueType>(null);
  const [isEdit, setIsEdit] = useState(-1);
  const [isEditFirstName, setIsEditFirstName] = useState<boolean>(false);
  const [isEditLastName, setIsEditLastName] = useState<boolean>(false);
  const [isEditPhone, setIsEditPhone] = useState<boolean>(false);
  const [isEditEmail, setIsEditEmail] = useState<boolean>(false);
  const [isEditAddress, setIsEditAddress] = useState<boolean>(false);
  const [isButtonLoading, setIsButtonLoading] = useState<number>(-1);
  const [isUploadLoading, setIsUploadLoading] = useState<boolean>(false);
  const [isImgErr, setIsImgErr] = useState<boolean>(false);
  const [errMsg, setErrMsg] = useState<ErrorMessage>({
    flag: false,
    message: null,
    index: -1,
  });
  const [msg, setMsg] = useState<ToastState>({
    variant: "",
    content: null,
  });
  let data = {
    first_name: userData?.data?.accountData?.first_name ?? "",
    last_name: userData?.data?.accountData?.last_name ?? "",
    email: userData?.data?.accountData?.email ?? "",
    phone: userData?.data?.accountData?.phone ?? "",
    country: userData?.data?.accountData?.country ?? "",
    postal_code: userData?.data?.accountData?.postal_code ?? "",
    country_code: userData?.data?.accountData?.country_code ?? "",
    city: userData?.data?.accountData?.city ?? "",
    state: userData?.data?.accountData?.state ?? "",
    address1: userData?.data?.accountData?.address1 ?? "",
    address2: userData?.data?.accountData?.address2 ?? "",
    phone_type:
      userData?.data?.accountData?.phone_type &&
      userData?.data?.accountData?.phone_type !== ""
        ? userData?.data?.accountData?.phone_type
        : "other",
  };

  const [editData, setEditData] = useState<EditData>(data);
  const [showOtpModal, setShowOtpModal] = useState<ShowOtpModal>({
    flag: false,
    value: null,
    data: null,
  });

  const options: OptionItem[] = [
    {
      id: 1,
      name: "Mobile",
      value: "mobile",
      icon: <MobileIcon />,
    },
    {
      id: 2,
      name: "Home",
      value: "home",
      icon: <HomeIcon />,
    },
    {
      id: 3,
      name: "Work",
      value: "work",
      icon: <WorkIcon />,
    },
    {
      id: 4,
      name: "Other",
      value: "other",
      icon: <OtherIcon />,
    },
  ];

  const { countryList } = useAppSelector(
    (state: RootState) => state.paymentMethod
  );

  const portalSettings = usePortalSettingsData();
  const onCountryListGet = async () => {
    try {
      await dispatch(countryListGet()).unwrap();
    } catch (error: any) {
      console.log("conntry list get err: -->", error?.message);
    }
  };

  const [addressLoading, setAddressLoading] = useState<boolean>(false);

  useEffect(() => {
    if (countryList.length === 0) {
      onCountryListGet();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryList]);

  const handleCancel = () => {
    setIsEditFirstName(false);
    setIsEditLastName(false);
    setIsEditPhone(false);
    setIsEditEmail(false);
    setIsEditAddress(false);
    setIsEdit(-1);
    setIsButtonLoading(-1);
    setValue(null);
    setErrMsg({
      flag: false,
      message: null,
      index: -1,
    });
  };

  const handleAddressSave = async (data: Partial<EditData>) => {
    setAddressLoading(true);
    let accountData = { ...userData.data.accountData };
    // Each check has to treat a missing field and an empty one the same way.
    // Guarding with `data?.field && ...` let a cleared field (empty string) skip
    // validation entirely and save blank.
    const isBlank = (field?: string) => !field || field.trim() === "";
    const missingField = ADDRESS_REQUIRED_FIELDS.find(([field]) =>
      isBlank(data?.[field])
    );

    if (missingField) {
      setErrMsg({ flag: true, message: missingField[1], index: 5 });
      setAddressLoading(false);
      return;
    }
    let payload = {
      address1: data.address1,
      city: data.city,
      state: data.state,
      country: data.country,
      address2: data.address2,
      postal_code: data.postal_code,
    };

    try {
      const res: any = await dispatch(accountDataUpdate(payload)).unwrap();
      if (!res?.body?.success) {
        // Applying and closing used to sit in `finally`, so a rejected save
        // still showed the new address and shut the form — the change looked
        // saved until the next reload. Both now happen only on success.
        setErrMsg({
          flag: true,
          message: res?.body?.data?.msg || SAVE_FAILED_MESSAGES[5],
          index: 5,
        });
        return;
      }
      await dispatch(
        addNotification({
          initiated_by: "contact",
          event_type: "contact",
          userid: userData?.data?.accountData?.leadData?.user_id,
          email: "",
          event_status: "modified",
          activity_type: "contact_log",
          clusterid: clusterId,
          communication_status: "",
          accounts_id: userData?.data?.accountData?.id,
        })
      );
      setEditData((state: EditData) => ({ ...state, ...data }));
      accountData = { ...accountData, ...data };
      await dispatch(updateUserData(accountData));
      setValue(null);
      handleCancel();
    } catch (err) {
      console.log("err =====", err);
      setErrMsg({ flag: true, message: SAVE_FAILED_MESSAGES[5], index: 5 });
    } finally {
      setAddressLoading(false);
    }
  };

  const parseNumber = (inputPhoneNumber: string | undefined) => {
    try {
      if (!inputPhoneNumber) {
        return;
      }
      const parsedPhoneNumber = parsePhoneNumber(inputPhoneNumber);
      if (parsedPhoneNumber) {
        return {
          countryCode: parsedPhoneNumber?.countryCallingCode || "",
          phoneNumber: parsedPhoneNumber?.nationalNumber || "",
        };
      } else {
        return {
          countryCode: "",
          phoneNumber: "",
        };
      }
    } catch (error) {
      console.error("Error parsing phone number:", error);
      return null;
    }
  };

  const normalizeCountryCode = (countryCode?: string | number) =>
    String(countryCode ?? "").replace(/\D/g, "");

  const isPhoneNumberValid = (phone?: string) => {
    try {
      return Boolean(phone) && isValidPhoneNumber(phone as string);
    } catch (error) {
      return false;
    }
  };

  const getEditablePhoneNumber = (phone?: string, countryCode?: string) => {
    const normalizedPhone = String(phone ?? "").replace(/\D/g, "");
    if (!normalizedPhone) return "";

    const explicitCountryCode = normalizeCountryCode(countryCode);
    const storedE164Phone = `+${normalizedPhone}`;

    if (!explicitCountryCode && isPhoneNumberValid(storedE164Phone)) {
      return storedE164Phone;
    }

    const normalizedCountryCode = explicitCountryCode || "1";

    return `+${normalizedCountryCode}${normalizedPhone}`;
  };

  const getDisplayPhoneNumber = (phone?: string, countryCode?: string) => {
    const editablePhone = getEditablePhoneNumber(phone, countryCode);
    if (!editablePhone) return "--";

    const parsedPhone = parseNumber(editablePhone);
    if (!parsedPhone?.countryCode || !parsedPhone.phoneNumber) {
      return editablePhone;
    }

    const formattedPhone = formatNumber(
      parsedPhone.phoneNumber,
      parsedPhone.countryCode
    );

    return formattedPhone || editablePhone;
  };

  const handlePhoneInput = () => {
    const phoneCandidate = value?.phone?.trim() || "";

    if (!phoneCandidate || !isPhoneNumberValid(phoneCandidate)) {
      setErrMsg({
        flag: true,
        message: phoneCandidate ? "Phone number is invalid" : REQUIRED_MESSAGES[4],
        index: 4,
      });
      return;
    }

    const parsedNumber = parseNumber(phoneCandidate);
    if (parsedNumber?.countryCode && parsedNumber?.phoneNumber) {
      let { countryCode, phoneNumber } = parsedNumber;
      let newPhone = phoneNumber;
      let newCode = countryCode;
      let newType = value?.phone_type;
      const existingPhone = getEditablePhoneNumber(
        data.phone,
        data.country_code
      );
      const normalizedCandidatePhone = `+${newCode}${newPhone}`;
      if (
        normalizedCandidatePhone === existingPhone &&
        newType === data.phone_type
      ) {
        setValue(null);
        handleCancel();
        return;
      }
      data.phone_type = newType;
      data.country_code = newCode;
      data.phone = newPhone;
      setIsButtonLoading(-1);
      setShowOtpModal((state: ShowOtpModal) => ({
        ...state,
        flag: true,
        data: data as EditData,
        value: "phone",
      }));
    } else {
      setErrMsg({
        flag: true,
        message: "Phone number is invalid",
        index: 4,
      });
      return;
    }
  };

  const handleEdit = async () => {
    let data = { ...editData };
    let accountData = { ...userData.data.accountData };
    let payload: any = {};
    setErrMsg({ flag: false, message: null, index: -1 });
    setIsButtonLoading(isEdit);

    const notifyPayloadBase = {
      initiated_by: "contact",
      event_type: "contact",
      userid: userData?.data?.accountData?.leadData?.user_id,
      email: "",
      event_status: "modified",
      activity_type: "contact_log",
      clusterid: clusterId,
      communication_status: "",
      accounts_id: userData?.data?.accountData?.id,
    };

    try {
      switch (isEdit) {
        case 1: {
          const first = ((value as Partial<EditData>)?.first_name || "").trim();
          if (first.length === 0) {
            setErrMsg({
              flag: true,
              message: REQUIRED_MESSAGES[1],
              index: 1,
            });
            setIsButtonLoading(-1);
            return;
          }
          if (first === data.first_name) {
            setValue(null);
            handleCancel();
            break;
          }
          data.first_name = first;
          accountData = { ...accountData, ...data };
          payload = { first_name: first };

          const res: any = await dispatch(accountDataUpdate(payload)).unwrap();
          if (!res?.body?.success) {
            showSaveError(1, res?.body?.data?.msg);
            break;
          }
          setEditData(data);
          await dispatch(addNotification(notifyPayloadBase));
          await dispatch(updateUserData(accountData));
          setValue(null);
          handleCancel();
          break;
        }
        case 2: {
          const last = ((value as Partial<EditData>)?.last_name || "").trim();
          if (last.length === 0) {
            setErrMsg({
              flag: true,
              message: REQUIRED_MESSAGES[2],
              index: 2,
            });
            setIsButtonLoading(-1);
            return;
          }
          if (last === data.last_name) {
            setValue(null);
            handleCancel();
            break;
          }
          data.last_name = last;
          accountData = { ...accountData, ...data };
          payload = { last_name: last };

          const res2: any = await dispatch(accountDataUpdate(payload)).unwrap();
          if (!res2?.body?.success) {
            showSaveError(2, res2?.body?.data?.msg);
            break;
          }
          setEditData(data);
          await dispatch(addNotification(notifyPayloadBase));
          await dispatch(updateUserData(accountData));
          setValue(null);
          handleCancel();
          break;
        }
        case 3: {
          const emailCandidate = (
            (value as Partial<EditData>)?.email || ""
          ).trim();
          if (emailCandidate.length === 0 || !validateEmail(emailCandidate)) {
            setErrMsg({
              flag: true,
              message:
                emailCandidate.length === 0
                  ? REQUIRED_MESSAGES[3]
                  : "Email is invalid",
              index: 3,
            });
            setIsButtonLoading(-1);
            return;
          }
          if (emailCandidate === data.email) {
            setValue(null);
            handleCancel();
            break;
          }
          data.email = emailCandidate;
          payload = { isEmail: true, email: data.email };
          const sent: any = await dispatch(contactVerification(payload)).unwrap();
          if (sent && sent?.body && sent.body.success === false) {
            showSaveError(3, sent?.body?.data?.msg);
            break;
          }
          // Don't write the new email into the profile yet: the code has only
          // been sent, not entered. OTPVerificationModal applies it once the
          // code is confirmed.
          setShowOtpModal((state: ShowOtpModal) => ({
            ...state,
            flag: true,
            data: data as EditData,
            value: "email",
          }));
          break;
        }
        case 4:
          handlePhoneInput();
          break;
        default:
          setValue(null);
          handleCancel();
      }
    } catch (err) {
      console.log("err =====", err);
      showSaveError(isEdit);
    } finally {
      setIsButtonLoading(-1);
    }
  };

  const getCountryShortName = (phoneNumber?: string | number) => {
    try {
      const idx =
        typeof phoneNumber === "string" ? parseInt(phoneNumber) : phoneNumber;
      const parsedPhoneNumber = countryCodes[Number(idx)];
      return parsedPhoneNumber ?? "US";
    } catch (error) {
      console.error("Error parsing phone number:", error);
      return "US";
    }
  };

  const handleOnPhoneChange = (phone: string) => {
    applyValueChange({ phone });
  };

  // Rethrow rather than swallowing: returning undefined here made the upload
  // handler bail out silently, so an unreadable or unsupported image produced
  // no error, no toast and no visible change at all.
  const compressImage = async (file: File) => {
    const options = {
      maxSizeMB: 0.5,
      maxWidthOrHeight: 1920,
      useWebWorker: true,
    };
    return await imageCompression(file, options);
  };

  const handleImageUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setMsg({
      content: null,
      variant: "",
    });
    if (file) {
      setIsUploadLoading(true);
      try {
        const compressedFile = await compressImage(file);
        if (!compressedFile) {
          throw new Error("Image could not be processed");
        }
        const formData = new FormData();
        formData.append("profile", compressedFile, compressedFile.name);
        let res: any = await dispatch(updateProfilePicture(formData));
        if (res?.payload?.body?.success) {
          setMsg({
            content: "Your profile image updated successfully",
            variant: "success",
          });
          await dispatch(
            addNotification({
              initiated_by: "contact",
              event_type: "contact",
              userid: userData?.data?.accountData?.leadData?.user_id,
              email: "",
              event_status: "modified",
              activity_type: "contact_log",
              clusterid: clusterId,
              communication_status: "",
              accounts_id: userData?.data?.accountData?.id,
            })
          );
        } else {
          setMsg({
            content: "Failed to upload image. please try again",
            variant: "error",
          });
        }
      } catch (error) {
        setMsg({
          content: "Failed to upload image. please try again",
          variant: "error",
        });
        console.error("Error uploading image:", error);
      } finally {
        updateImageSRC();
        setIsUploadLoading(false);
      }
    }
  };
  const updateImageSRC = () => {
    setIsImgErr(false);
    let profileImage = document.getElementById(
      "user-profile-image"
    ) as HTMLImageElement | null;
    if (profileImage) {
      profileImage.src = `${PROFILE_URL}/contact-assets/${
        userData?.data?.accountData?.id
      }?t=${Date.now()}`;
    }

    let sideMenuProfileImageOne = document.getElementById(
      "sidemenu-profile-image-1"
    ) as HTMLImageElement | null;
    if (sideMenuProfileImageOne) {
      sideMenuProfileImageOne.src = `${PROFILE_URL}/contact-assets/${
        userData?.data?.accountData?.id
      }?t=${Date.now()}`;
    }

    let sideMenuProfileImageTwo = document.getElementById(
      "sidemenu-profile-image-2"
    ) as HTMLImageElement | null;
    if (sideMenuProfileImageTwo) {
      sideMenuProfileImageTwo.src = `${PROFILE_URL}/contact-assets/${
        userData?.data?.accountData?.id
      }?t=${Date.now()}`;
    }

    let navMenuProfileImageOne = document.getElementById(
      "navmenu-profile-image-1"
    ) as HTMLImageElement | null;
    if (navMenuProfileImageOne) {
      navMenuProfileImageOne.src = `${PROFILE_URL}/contact-assets/${
        userData?.data?.accountData?.id
      }?t=${Date.now()}`;
    }

    let navMenuProfileImageTwo = document.getElementById(
      "navmenu-profile-image-2"
    ) as HTMLImageElement | null;
    if (navMenuProfileImageTwo) {
      navMenuProfileImageTwo.src = `${PROFILE_URL}/contact-assets/${
        userData?.data?.accountData?.id
      }?t=${Date.now()}`;
    }
    try {
      window.dispatchEvent(
        new CustomEvent("profile-image-updated", {
          detail: {
            id: userData?.data?.accountData?.id,
            t: Date.now(),
          },
        })
      );
    } catch (e) {
      // ignore if CustomEvent isn't supported in environment
    }
  };
  const handleInputClick = () => {
    if (photoInput && photoInput.current) {
      photoInput?.current.click();
    }
  };

  const handleEditableActionKeyDown = (
    event: KeyboardEvent<HTMLElement>,
    action: () => void
  ) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      action();
    }
  };

  const updateEditData = (data: Partial<EditData>) => {
    setEditData((state: EditData) => ({ ...state, ...data }));
  };

  // The address form is the tallest editor on the page and its Save/Cancel row
  // opens below the fold on a standard desktop window. Bring the end of the
  // section into view so the buttons are reachable without hunting for them.
  useEffect(() => {
    if (isEdit !== 5) return;
    const node = addressSectionRef.current;
    if (!node) return;

    // "instant", not "smooth": the observer below fires repeatedly while the
    // country and state lists load, and each smooth scroll cancels the one
    // before it, so on a phone the form never actually moves.
    const bringEndIntoView = () =>
      node.scrollIntoView({ block: "end", behavior: "instant" as ScrollBehavior });

    bringEndIntoView();

    // The form keeps growing after it opens as the country and state lists
    // resolve, so a single scroll lands short. Track its height until it
    // settles.
    const observer = new ResizeObserver(bringEndIntoView);
    observer.observe(node);
    return () => observer.disconnect();
  }, [isEdit]);

  const isBlankValue = (field?: string) => !field || field.trim() === "";

  // A field's validation message belongs directly under its input, and the input
  // itself turns red — matching how the OTP dialog already reports errors.
  const hasFieldError = (index: number) => errMsg.flag && errMsg.index === index;

  const fieldErrorClass = (index: number) =>
    hasFieldError(index) ? "!border-red hover:!border-red" : "";

  const renderFieldError = (index: number) =>
    hasFieldError(index) ? (
      <div className="text-sm text-red flex justify-start gap-[5px]">
        <InfoRedCircleIcon />
        {errMsg.message}
      </div>
    ) : null;

  // Save stays disabled while a required field is empty, which on its own gives
  // no reason why. Leaving the field empty surfaces the message instead; typing
  // clears it again via applyValueChange.
  // Surface a save failure in the same place validation errors appear, and
  // leave the editor open so the entered value isn't lost.
  const showSaveError = (index: number, serverMessage?: string) => {
    setErrMsg({
      flag: true,
      message: serverMessage || SAVE_FAILED_MESSAGES[index],
      index,
    });
  };

  const handleRequiredBlur = (index: number, fieldValue?: string) => {
    if (!isBlankValue(fieldValue)) return;
    setErrMsg({ flag: true, message: REQUIRED_MESSAGES[index], index });
  };

  // The phone widget always carries a "+(code)" prefix, so emptiness has to be
  // judged on the national digits alone.
  const handlePhoneBlur = () => {
    const phone = (value as Partial<EditData>)?.phone || "";
    const nationalDigits = phone.replace(/^\+\(\d+\)/, "").replace(/\D/g, "");
    if (!nationalDigits) {
      setErrMsg({ flag: true, message: REQUIRED_MESSAGES[4], index: 4 });
      return;
    }
    if (!isPhoneNumberValid(phone)) {
      setErrMsg({ flag: true, message: "Phone number is invalid", index: 4 });
    }
  };

  // Editing a field clears any validation message it produced. Without this the
  // "… is required" error stays on screen after the field has been corrected.
  const applyValueChange = (data: Partial<EditData>) => {
    setValue((state) => ({ ...(state as Partial<EditData>), ...data }));
    setErrMsg((state) =>
      state.flag ? { flag: false, message: null, index: -1 } : state
    );
  };

  // The stored phone has no separate country code, so derive it from the E.164
  // number itself. Defaulting to "1" made every foreign number open as a US one.
  const openPhoneEditor = () => {
    const editablePhone = getEditablePhoneNumber(
      editData?.phone,
      editData?.country_code
    );
    const parsedPhone = parseNumber(editablePhone);
    setValue({
      phone_type: editData?.phone_type || "other",
      phone: editablePhone,
      country_code: normalizeCountryCode(
        editData.country_code || parsedPhone?.countryCode || "1"
      ),
      country: editData.country,
    });
    setIsEdit(4);
    setIsEditPhone(false);
  };

  return (
    <div
      className="h-full w-full lg:block flex flex-col  lg:overflow-hidden overflow-y-auto"
      {...rootAttrs}
    >
      <Header
        headerContent={
          <div
            className="w-full h-fit lg:text-[32px] sm:text-3xl text-2xl leading-[44px] text-txtPrimary font-medium -tracking-[0.72px] lg:p-0 p-5"
            {...titleAttrs}
          >
            My Profile
          </div>
        }
      />
      <div
        className="lg:pt-[93px] flex flex-col gap-10 lg:px-10 px-5 h-full lg:overflow-y-scroll xl:pb-10 lg:pb-20 pb-10"
        {...contentAttrs}
      >
        <div className="lg:mt-10 relative" {...avatarAttrs}>
          <>
            {!isImgErr ? (
              <div
                className="w-[100px] h-[100px] lg:w-[150px] lg:h-[150px] rounded-full cursor-pointer flex items-center justify-center overflow-clip"
                onClick={() => handleInputClick()}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    handleInputClick();
                  }
                }}
                role="button"
                tabIndex={0}
                data-testid={tid("avatar-image-button")}
                aria-label="Upload profile photo"
              >
                <img
                  id="user-profile-image"
                  className="block h-full w-full rounded-full border-1px border-gray object-cover object-center"
                  src={`${PROFILE_URL}/contact-assets/${
                    userData?.data?.accountData?.id
                  }?t=${Date.now()}`}
                  onError={(e) => {
                    setIsImgErr(true);
                  }}
                  alt="Profile"
                />{" "}
              </div>
            ) : (
              <div
                onClick={() => {
                  if (!isUploadLoading) {
                    handleInputClick();
                  }
                }}
                className="w-[100px] h-[100px] lg:w-[150px] lg:h-[150px] rounded-full cursor-pointer flex justify-center items-center lg:text-7xl text-4xl bg-bgSecondary text-iconBright"
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    if (!isUploadLoading) {
                      handleInputClick();
                    }
                  }
                }}
                role="button"
                tabIndex={0}
                data-testid={tid("avatar-placeholder-button")}
                aria-label="Upload profile photo"
              >
                {data?.first_name || data?.last_name ? (
                  getInitialsLatters(data?.first_name, data?.last_name)
                ) : (
                  <img
                    src={UserImg}
                    alt="Default avatar"
                    className="w-[100px] h-[100px] lg:w-[150px] lg:h-[150px] rounded-full cursor-pointer flex justify-center items-center lg:text-7xl text-4xl bg-txtParagraph text-bgPrimary"
                  />
                )}
              </div>
            )}

            <div
              className="cursor-pointer absolute bottom-1 lg:left-28 left-[76px] lg:w-11 lg:h-11 w-10 h-10 rounded-full border-[2px] border-bgPrimary bg-bgSecondary grid place-items-center"
              onClick={() => {
                if (!isUploadLoading) {
                  handleInputClick();
                }
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  if (!isUploadLoading) {
                    handleInputClick();
                  }
                }
              }}
              role="button"
              tabIndex={0}
              data-testid={tid("avatar-upload-button")}
              aria-label="Upload profile photo"
            >
              {isUploadLoading ? (
                <div className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-solid border-blue border-r-transparent align-[-0.125em] text-primary motion-reduce:animate-[spin_1.5s_linear_infinite]"></div>
              ) : (
                <CameraIcon aria-hidden="true" />
              )}
            </div>
            <input
              id="photo-input"
              ref={photoInput}
              accept="image/png, image/jpeg, image/jpg"
              type="file"
              className="hidden"
              onChange={handleImageUpload}
              data-testid={tid("avatar-file-input")}
              aria-label="Choose profile photo"
            />
          </>
        </div>
        {/* lg:-ml-3 cancels the 12px padding on each field panel so labels and
            values line up with the page title and the avatar, as in the desktop
            design. Mobile keeps the indent — the mobile design specifies it. */}
        <div
          className="xl:max-w-[668px] w-full flex flex-col lg:gap-10 gap-6 xl:justify-center xl:items-center lg:mb-[50px] lg:-ml-3"
          {...fieldsAttrs}
        >
          <div className="flex xl:flex-row flex-col lg:gap-5 gap-6">
            <div
              className={`lg:w-[324px] p-3 flex flex-col gap-[8px] cursor-pointer rounded-md ${
                (isEditFirstName || isEdit === 1) && "bg-bgHover"
              } relative`}
              onMouseEnter={() => isEdit === -1 && setIsEditFirstName(true)}
              onMouseLeave={() => setIsEditFirstName(false)}
              data-testid={tid("field-first-name")}
            >
              <span
                className={`absolute text-blue hover:!text-btnPrimaryHover underline right-3 top-[13px] text-sm ${
                  isEditFirstName
                    ? "block"
                    : isEdit === 1
                      ? "hidden"
                      : EDIT_LINK_IDLE_CLASS
                }`}
                onClick={() => {
                  setIsEdit(1);
                  setIsEditFirstName(false);
                  setValue({ first_name: editData?.first_name });
                }}
                onKeyDown={(event) =>
                  handleEditableActionKeyDown(event, () => {
                    setIsEdit(1);
                    setIsEditFirstName(false);
                    setValue({ first_name: editData?.first_name });
                  })
                }
                role="button"
                tabIndex={0}
                data-testid={tid("field-first-name-edit-button")}
                aria-label="Edit first name"
              >
                Edit
              </span>

              <div className="w-full text-base leading-[20.83px] text-txtSecondary">
                First Name
              </div>
              {isEdit !== 1 ? (
                <div className="w-full lg:text-xl text-lg leading-[26.04px] text-txtPrimary leading-">
                  {editData?.first_name || "--"}
                </div>
              ) : (
                <>
                  <InputField
                    name="first_name"
                    label=""
                    className={`!py-[6px] !px-3 ${fieldErrorClass(1)}`}
                    _className="!text-xl !font-medium !leading-normal"
                    placeholder="Enter first name"
                    value={(value as Partial<EditData>)?.first_name || ""}
                    data-testid={tid("field-first-name-input")}
                    aria-label="First name"
                    onChange={(e) =>
                      applyValueChange({ first_name: e.target.value })
                    }
                    onBlur={() =>
                      handleRequiredBlur(
                        1,
                        (value as Partial<EditData>)?.first_name
                      )
                    }
                  />
                  {renderFieldError(1)}
                  <div className="flex gap-5 mt-3">
                    <Button
                      isLoading={isButtonLoading === 1}
                      text={"Save"}
                      onClick={handleEdit}
                      data-testid={tid("field-first-name-save-button")}
                      aria-label="Save first name"
                      isDisabled={
                        isButtonLoading === 1 ||
                        isBlankValue((value as Partial<EditData>)?.first_name)
                      }
                    ></Button>
                    <Button
                      isDisabled={isButtonLoading === 1}
                      text={"Cancel"}
                      variant={"white"}
                      data-testid={tid("field-first-name-cancel-button")}
                      aria-label="Cancel first name edit"
                      onClick={() => {
                        handleCancel();
                      }}
                    ></Button>
                  </div>
                </>
              )}
            </div>

            <div
              className={`lg:w-[324px] w-full p-3 flex flex-col gap-[8px] cursor-pointer rounded-md ${
                (isEditLastName || isEdit === 2) && "bg-bgHover"
              } relative`}
              onMouseEnter={() => isEdit === -1 && setIsEditLastName(true)}
              onMouseLeave={() => setIsEditLastName(false)}
              data-testid={tid("field-last-name")}
            >
              <span
                className={`absolute text-blue hover:!text-btnPrimaryHover underline right-3 top-[13px] text-sm ${
                  isEditLastName
                    ? "block"
                    : isEdit === 2
                      ? "hidden"
                      : EDIT_LINK_IDLE_CLASS
                }`}
                onClick={() => {
                  setIsEdit(2);
                  setIsEditLastName(false);
                  setValue({ last_name: editData.last_name });
                }}
                onKeyDown={(event) =>
                  handleEditableActionKeyDown(event, () => {
                    setIsEdit(2);
                    setIsEditLastName(false);
                    setValue({ last_name: editData.last_name });
                  })
                }
                role="button"
                tabIndex={0}
                data-testid={tid("field-last-name-edit-button")}
                aria-label="Edit last name"
              >
                Edit
              </span>

              <div className="w-full text-base leading-[20.83px] text-txtSecondary">
                Last Name
              </div>
              {isEdit !== 2 ? (
                <div className="w-full  lg:text-xl text-lg leading-[26.04px] text-txtPrimary">
                  {editData?.last_name || "--"}
                </div>
              ) : (
                <>
                  <InputField
                    name="last_name"
                    label=""
                    className={`!py-[6px] !px-3 ${fieldErrorClass(2)}`}
                    _className="!text-xl !font-medium !leading-normal"
                    placeholder="Enter last name"
                    value={(value as Partial<EditData>)?.last_name || ""}
                    data-testid={tid("field-last-name-input")}
                    aria-label="Last name"
                    onChange={(e) =>
                      applyValueChange({ last_name: e.target.value })
                    }
                    onBlur={() =>
                      handleRequiredBlur(
                        2,
                        (value as Partial<EditData>)?.last_name
                      )
                    }
                  />
                  {renderFieldError(2)}
                  <div className="flex gap-5 mt-3">
                    <Button
                      isLoading={isButtonLoading === 2}
                      text={"Save"}
                      onClick={handleEdit}
                      data-testid={tid("field-last-name-save-button")}
                      aria-label="Save last name"
                      isDisabled={
                        isButtonLoading === 2 ||
                        isBlankValue((value as Partial<EditData>)?.last_name)
                      }
                    ></Button>
                    <Button
                      isDisabled={isButtonLoading === 2}
                      text={"Cancel"}
                      variant={"white"}
                      data-testid={tid("field-last-name-cancel-button")}
                      aria-label="Cancel last name edit"
                      onClick={() => {
                        handleCancel();
                      }}
                    ></Button>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="flex xl:flex-row flex-col gap-5">
            <div
              className={`lg:w-[324px] p-3 flex flex-col gap-[8px] cursor-pointer rounded-md ${
                (isEditEmail || isEdit === 3) && "bg-bgHover"
              } relative`}
              onMouseEnter={() => isEdit === -1 && setIsEditEmail(true)}
              onMouseLeave={() => setIsEditEmail(false)}
              data-testid={tid("field-email")}
            >
              {portalSettings?.email_allow_editing === SettingsValue.YES && (
                <span
                  className={`absolute text-blue hover:!text-btnPrimaryHover underline right-3 top-[13px] text-sm ${
                    isEditEmail
                      ? "block"
                      : isEdit === 3
                        ? "hidden"
                        : EDIT_LINK_IDLE_CLASS
                  }`}
                  onClick={() => {
                    setIsEdit(3);
                    setIsEditEmail(false);
                    setValue({ email: editData.email });
                  }}
                  onKeyDown={(event) =>
                    handleEditableActionKeyDown(event, () => {
                      setIsEdit(3);
                      setIsEditEmail(false);
                      setValue({ email: editData.email });
                    })
                  }
                  role="button"
                  tabIndex={0}
                  data-testid={tid("field-email-edit-button")}
                  aria-label="Edit email"
                >
                  Edit
                </span>
              )}

              <div className="w-full text-base leading-[20.83px] text-txtSecondary">
                Email
              </div>
              {isEdit !== 3 ? (
                <div className="w-full lg:text-xl text-lg leading-[26.04px] text-txtPrimary flex items-center gap-[8px] ">
                  <span className="w-[26px] h-[26px] bg-btnSmall grid place-items-center rounded-full">
                    <SmsIcon aria-hidden="true" />
                  </span>
                  <p title={editData?.email} className="truncate">
                    {editData?.email || "--"}
                  </p>
                </div>
              ) : (
                <>
                  <InputField
                    name="email"
                    label=""
                    className={`!py-[6px] !px-3 ${fieldErrorClass(3)}`}
                    _className="!text-xl !font-medium !leading-normal"
                    placeholder="Enter Email"
                    value={(value as Partial<EditData>)?.email || ""}
                    data-testid={tid("field-email-input")}
                    aria-label="Email"
                    onChange={(e) =>
                      applyValueChange({ email: e.target.value })
                    }
                    onBlur={() =>
                      handleRequiredBlur(3, (value as Partial<EditData>)?.email)
                    }
                  />
                  {renderFieldError(3)}
                  <div className="flex gap-5 mt-3">
                    <Button
                      isLoading={isButtonLoading === 3}
                      text={"Save"}
                      onClick={handleEdit}
                      data-testid={tid("field-email-save-button")}
                      aria-label="Save email"
                      isDisabled={
                        isButtonLoading === 3 ||
                        isBlankValue((value as Partial<EditData>)?.email)
                      }
                    ></Button>
                    <Button
                      isDisabled={isButtonLoading === 3}
                      text={"Cancel"}
                      variant={"white"}
                      data-testid={tid("field-email-cancel-button")}
                      aria-label="Cancel email edit"
                      onClick={() => {
                        handleCancel();
                      }}
                    ></Button>
                  </div>
                </>
              )}
            </div>

            <div
              className={`lg:w-[324px] p-3 flex flex-col gap-[8px] cursor-pointer rounded-md ${
                (isEditPhone || isEdit === 4) && "bg-bgHover"
              } relative`}
              onMouseEnter={() => isEdit === -1 && setIsEditPhone(true)}
              onMouseLeave={() => setIsEditPhone(false)}
              data-testid={tid("field-phone")}
            >
              {portalSettings?.verify_phone_allow_editing ===
                SettingsValue.YES && (
                <span
                  className={`absolute text-blue hover:!text-btnPrimaryHover underline right-3 top-[13px] text-sm ${
                    isEditPhone
                      ? "block"
                      : isEdit === 4
                        ? "hidden"
                        : EDIT_LINK_IDLE_CLASS
                  }`}
                  onClick={openPhoneEditor}
                  onKeyDown={(event) =>
                    handleEditableActionKeyDown(event, openPhoneEditor)
                  }
                  role="button"
                  tabIndex={0}
                  data-testid={tid("field-phone-edit-button")}
                  aria-label="Edit phone number"
                >
                  Edit
                </span>
              )}

              <div className="w-full text-base leading-[20.83px] text-txtSecondary">
                Phone Number
              </div>
              {isEdit !== 4 ? (
                <div className="w-full lg:text-xl text-lg leading-[26.04px] text-txtPrimary items-center flex gap-[8px]">
                  <span className="w-[26px] h-[26px] bg-btnSmall grid place-items-center rounded-full">
                    {editData?.phone_type &&
                      getPhoneIcon(editData.phone_type.toLowerCase())}
                  </span>
                  <>
                    {getDisplayPhoneNumber(
                      editData?.phone,
                      editData?.country_code
                    )}
                  </>
                </div>
              ) : (
                <>
                  <InputSelect
                    isSearchFeature={false}
                    label={""}
                    className={"!py-[6px] !px-3"}
                    arrowClassName={"!top-3"}
                    placeholder={"Select Phone Type"}
                    options={options}
                    selected={(value as Partial<EditData>)?.phone_type}
                    data-testid={tid("field-phone-type-select")}
                    aria-label="Phone type"
                    setSelected={(val: OptionItem | null) => {
                      setValue((state) => ({
                        ...(state as Partial<EditData>),
                        phone_type: (val?.value as PhoneType) || "other",
                      }));
                    }}
                  />
                  <div>
                    <MobileInputNew
                      inputValue={(value as Partial<EditData>)?.phone || ""}
                      userCountry={
                        getCountryShortName(
                          (value as Partial<EditData>).country_code
                        ) || "US"
                      }
                      setCountry={(itm: string) => {
                        setValue((state) => ({
                          ...(state as Partial<EditData>),
                          country_code: getCountryCallingCode(
                            itm as CountryCode
                          ),
                        }));
                      }}
                      setInputValue={(e: string) => {
                        handleOnPhoneChange(e);
                      }}
                      hasError={hasFieldError(4)}
                      onBlur={handlePhoneBlur}
                      data-testid={tid("field-phone-number-input")}
                      aria-label="Phone number"
                    />
                  </div>
                  {renderFieldError(4)}
                  <div className="flex gap-5 mt-3">
                    <Button
                      isLoading={isButtonLoading === 4}
                      text={"Save"}
                      onClick={handleEdit}
                      data-testid={tid("field-phone-save-button")}
                      aria-label="Save phone number"
                      isDisabled={
                        !value?.phone || !isPhoneNumberValid(value.phone)
                      }
                    ></Button>
                    <Button
                      isDisabled={isButtonLoading === 4}
                      text={"Cancel"}
                      variant={"white"}
                      data-testid={tid("field-phone-cancel-button")}
                      aria-label="Cancel phone edit"
                      onClick={() => {
                        handleCancel();
                      }}
                    ></Button>
                  </div>
                </>
              )}
            </div>
          </div>

          <div
            className={`xl:max-w-[668px] w-full p-3 flex flex-col gap-[8px] cursor-pointer rounded-md ${isEdit === 5 ? "mb-8" : ""} ${
              (isEditAddress || isEdit === 5) && "bg-bgHover"
            } relative`}
            onMouseEnter={() => isEdit === -1 && setIsEditAddress(true)}
            onMouseLeave={() => setIsEditAddress(false)}
            data-testid={tid("field-address")}
            ref={addressSectionRef}
          >
            {portalSettings?.mail_address_allow_editing ===
              SettingsValue.YES && (
              <span
                className={`absolute text-blue hover:!text-btnPrimaryHover underline right-3 top-[13px] text-sm ${
                  isEditAddress
                    ? "block"
                    : isEdit === 5
                      ? "hidden"
                      : EDIT_LINK_IDLE_CLASS
                }`}
                onClick={() => {
                  setIsEdit(5);
                  setIsEditAddress(false);
                  setValue({
                    city: editData.city,
                    country: editData.country,
                    address1: editData.address1,
                    address2: editData.address2,
                    state: editData.state,
                    postal_code: editData.postal_code,
                  });
                }}
                onKeyDown={(event) =>
                  handleEditableActionKeyDown(event, () => {
                    setIsEdit(5);
                    setIsEditAddress(false);
                    setValue({
                      city: editData.city,
                      country: editData.country,
                      address1: editData.address1,
                      address2: editData.address2,
                      state: editData.state,
                      postal_code: editData.postal_code,
                    });
                  })
                }
                role="button"
                tabIndex={0}
                data-testid={tid("field-address-edit-button")}
                aria-label="Edit address"
              >
                Edit
              </span>
            )}
            <div className="w-full text-base leading-[20.83px] text-txtSecondary">
              Address
            </div>
            {isEdit !== 5 ? (
              <div className="w-full lg:text-xl text-lg text-txtPrimary flex gap-[8px]">
                <span className="w-[26px] h-[26px] bg-btnSmall grid place-items-center rounded-full">
                  <LocationIcon aria-hidden="true" />
                </span>
                <div className="lg:w-[610px]  w-[270px] whitespace-normal break-words">
                  {formatAddress(
                    editData?.state,
                    editData?.country,
                    editData?.city,
                    editData?.address1,
                    editData?.address2,
                    editData?.postal_code
                  )}
                </div>
              </div>
            ) : (
              <div
                data-testid={tid("field-address-form")}
                aria-label="Address form"
              >
                <AddressForm
                  {...value}
                  handleCancel={handleCancel}
                  handleSave={handleAddressSave}
                  errMsg={errMsg}
                  setErrMsg={setErrMsg}
                  addressLoading={addressLoading}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <OTPVerificationModal
        showModal={showOtpModal}
        setShowModal={setShowOtpModal}
        setEditData={updateEditData}
        editData={editData}
        handleCancel={handleCancel}
        accountData={userData.data.accountData}
      />

      <MessageToast
        variant={msg?.variant}
        content={msg?.content}
        isProgressBar
        width="max-w-[810px] px-5 w-full"
        className=" !absolute !right-1/2 !translate-x-1/2"
        onCloseClick={() => {
          setMsg({
            ...msg,
            variant: "",
            content: null,
          });
        }}
      />
    </div>
  );
};

const UserProfile = () => (
  <TestIdScope scope="profile">
    <UserProfileContent />
  </TestIdScope>
);

export default UserProfile;
